import { Alert, Box, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';

import type { IntakeFormConfiguration } from '../types';

export type SelectableIntakeService = NonNullable<IntakeFormConfiguration['products']>[number];

export const INTAKE_SERVICE_OPTIONS: {
  id: SelectableIntakeService;
  label: string;
  description: string;
}[] = [
  { id: 'jotbill', label: 'JotBill', description: 'Set up billing for your practice.' },
  { id: 'jotcred', label: 'JotCred', description: 'Prepare credentialing for your providers.' },
];

export function IntakeServiceSelectionCard({
  services,
  canManage,
  isSaving = false,
  onChange,
}: {
  services: SelectableIntakeService[];
  canManage: boolean;
  isSaving?: boolean;
  onChange: (services: SelectableIntakeService[]) => void;
}) {
  return (
    <Stack spacing={2}>
      <Box>
        <Typography component="h2" sx={{ fontSize: '16px', fontWeight: 650, lineHeight: 1.5 }}>
          Services for your practice
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: '14px', lineHeight: 1.5 }}>
          Choose JotBill, JotCred, or both to register your organization for intake. Your selection
          determines the information your practice needs to provide.
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 1.5,
        }}
      >
        {INTAKE_SERVICE_OPTIONS.map(service => (
          <FormControlLabel
            key={service.id}
            sx={{
              m: 0,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              px: 1,
              py: 0.5,
              alignItems: 'center',
            }}
            control={
              <Checkbox
                size="small"
                checked={services.includes(service.id)}
                disabled={!canManage || isSaving}
                onChange={(_event, checked) =>
                  onChange(
                    checked ? [...services, service.id] : services.filter(id => id !== service.id),
                  )
                }
              />
            }
            label={
              <Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 600, lineHeight: 1.5 }}>
                  {service.label}
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: '14px', lineHeight: 1.5 }}>
                  {service.description}
                </Typography>
              </Box>
            }
          />
        ))}
      </Box>
      {!canManage && (
        <Alert severity="info">
          Ask your practice administrator to choose or change services for your organization.
        </Alert>
      )}
      {isSaving && (
        <Typography role="status" sx={{ fontSize: '14px' }}>
          Saving services…
        </Typography>
      )}
    </Stack>
  );
}
